import type React from 'react';

import { AdornedInput, Adornment, Divider, FormField, Input, Label, Textarea } from '@zillow/constellation';
import { CurrencyInput } from '@zillow/zrs-comet';

import type { Section } from '../../../models';
import { ELECTRIC_VEHICLE_CHARGING } from '../../../constants';
import YesAndNoExpansion from '../../../../shared/components/Widgets/YesAndNoExpansion';
import { CURRENCY_INPUT_PLACEHOLDER } from '../../../constants/stringConstants';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

function ElectricVehicleCharging({ section, setSectionValues }: Props) {
  function onNoElectricVehicleCharging(): void {
    setSectionValues({
      [ELECTRIC_VEHICLE_CHARGING.FIELD_NAME.IS_ENABLED]: false,
    });
  }

  function onYesElectricVehicleCharging(): void {
    setSectionValues({
      [ELECTRIC_VEHICLE_CHARGING.FIELD_NAME.IS_ENABLED]: true,
    });
  }

  function onNoPersonalLiabilityInsurance(): void {
    setSectionValues({
      [ELECTRIC_VEHICLE_CHARGING.FIELD_NAME.PERSONAL_LIABILITY_INSURANCE]: false,
    });
  }

  function onYesPersonalLiabilityInsurance(): void {
    setSectionValues({
      [ELECTRIC_VEHICLE_CHARGING.FIELD_NAME.PERSONAL_LIABILITY_INSURANCE]: true,
    });
  }

  function onNoSeparatelyMetered(): void {
    setSectionValues({
      [ELECTRIC_VEHICLE_CHARGING.FIELD_NAME.SEPARATELY_METERED]: false,
    });
  }

  function onYesSeparatelyMetered(): void {
    setSectionValues({
      [ELECTRIC_VEHICLE_CHARGING.FIELD_NAME.SEPARATELY_METERED]: true,
    });
  }

  function onInstallationUseMaintenanceChange(event: React.FormEvent<HTMLInputElement>): void {
    const { value } = event.currentTarget;

    setSectionValues({ [ELECTRIC_VEHICLE_CHARGING.FIELD_NAME.INSTALLATION_USE_MAINTENANCE]: value });
  }

  function onPersonalLiabilityAmountChange(event: React.FormEvent<HTMLInputElement>): void {
    setSectionValues({
      [ELECTRIC_VEHICLE_CHARGING.FIELD_NAME.PERSONAL_LIABILITY_INSURANCE_AMOUNT]: event.currentTarget.value,
    });
  }

  function onServiceProviderChange(event: React.FormEvent<HTMLInputElement>): void {
    setSectionValues({
      [ELECTRIC_VEHICLE_CHARGING.FIELD_NAME.SERVICE_PROVIDER]: event.currentTarget.value,
    });
  }

  return (
    <FormField
      label={
        <Label size="lg">
          Is the tenant permitted to install and maintain an electric charging station on the property?
        </Label>
      }
      control={
        <YesAndNoExpansion
          yesHandler={onYesElectricVehicleCharging}
          noHandler={onNoElectricVehicleCharging}
          yesActive={section.getFieldValue(ELECTRIC_VEHICLE_CHARGING.FIELD_NAME.IS_ENABLED)}
        >
          <FormField
            marginBottom="sm"
            label={<Label>Is personal liability insurance required?</Label>}
            control={
              <YesAndNoExpansion
                yesHandler={onYesPersonalLiabilityInsurance}
                noHandler={onNoPersonalLiabilityInsurance}
                yesActive={section.getFieldValue(ELECTRIC_VEHICLE_CHARGING.FIELD_NAME.PERSONAL_LIABILITY_INSURANCE)}
              >
                <FormField
                  label={<Label id="Estimated-association-fees">Personal liability insurance coverage amount</Label>}
                  control={
                    <AdornedInput
                      leftAdornment={<Adornment>$</Adornment>}
                      input={
                        <CurrencyInput
                          placeholder={CURRENCY_INPUT_PLACEHOLDER}
                          onChange={onPersonalLiabilityAmountChange}
                          onBlur={onPersonalLiabilityAmountChange}
                          value={section.getFieldValue(
                            ELECTRIC_VEHICLE_CHARGING.FIELD_NAME.PERSONAL_LIABILITY_INSURANCE_AMOUNT,
                          )}
                        />
                      }
                    />
                  }
                />
              </YesAndNoExpansion>
            }
          />
          <Divider marginY="sm" />
          <FormField
            marginBottom="sm"
            label={<Label>Is the electric vehicle charging station separately metered?</Label>}
            control={
              <YesAndNoExpansion
                yesHandler={onYesSeparatelyMetered}
                noHandler={onNoSeparatelyMetered}
                yesActive={section.getFieldValue(ELECTRIC_VEHICLE_CHARGING.FIELD_NAME.SEPARATELY_METERED)}
              >
                <FormField
                  label={<Label>What service provider?</Label>}
                  control={
                    <Input
                      onChange={onServiceProviderChange}
                      value={section.getFieldValue(ELECTRIC_VEHICLE_CHARGING.FIELD_NAME.SERVICE_PROVIDER)}
                    />
                  }
                />
              </YesAndNoExpansion>
            }
          />
          <Divider marginY="sm" />
          <FormField
            optional
            label={
              <Label>
                What requirements do you have for installation, use, and maintenance of the electric vehicle station?
              </Label>
            }
            control={
              <Textarea
                value={section.getFieldValue(ELECTRIC_VEHICLE_CHARGING.FIELD_NAME.INSTALLATION_USE_MAINTENANCE)}
                onChange={onInstallationUseMaintenanceChange}
                resize="vertical"
              />
            }
          />
        </YesAndNoExpansion>
      }
    />
  );
}

ElectricVehicleCharging.description = 'Hack week electric vehicle charging question';

export default ElectricVehicleCharging;
